import { useState, useEffect, useRef, ReactNode } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import apiService from '../../../services/api';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';

interface KeywordPlannerProps {
  onTooltipShow: (title: string, content: ReactNode) => void;
  onLockSuccess?: () => void;
}

interface KeywordItem {
  id: number;
  keyword: string;
  volume: number;
  difficulty: string;
  difficulty_bucket: string;
  intent: string;
  keyword_type: string;
  type: string;
  score: number;
  source: string;
  funnel_stage: string;
  is_selected: boolean;
  is_active: boolean;
  is_local?: boolean;
  traffic_potential?: number;
  business_potential?: number;
  growth_trend_score?: number;
  segmentation_score?: number;
  universe_rank?: number;
  competition_index?: number;
  brand_relevance_score?: number;
  relevance_insight?: {
    rating: string;
  };
}

interface GenerationProgress {
  status?: string;
  step?: string;
  step_index?: number;
  total_steps?: number;
  percent?: number;
  title?: string;
  message?: string;
  detail?: string;
  detail_data?: Record<string, any>;
  icon?: string;
  tone?: string;
}

export default function KeywordPlanner({ onTooltipShow, onLockSuccess }: KeywordPlannerProps) {
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [locking, setLocking] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [inlineMsg, setInlineMsg] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState<GenerationProgress | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);

  const [keywordsObj, setKeywordsObj] = useState<Record<string, KeywordItem[]>>({});
  const [activeTab, setActiveTab] = useState<'seo' | 'gmb'>('seo');
  usePageTracking({
    moduleName: activeTab === 'gmb' ? 'GMB Manager' : 'SEO Manager',
    submoduleName: `Keyword Planner / ${activeTab.toUpperCase()}`,
    routePath: `/dashboard#keyword-planner/${activeTab}`,
  });
  const { trackAction } = useTelemetry({ module: MODULES.UTILITY_MANAGER, submodule: 'Keyword Planner' });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);

  const [filterVolume, setFilterVolume] = useState('all');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [relevanceFilter, setRelevanceFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('score');
  const [updatingKeywords, setUpdatingKeywords] = useState(false);
  const initTriggeredRef = useRef(false);
  const completionTimerRef = useRef<number | null>(null);

  const userId = localStorage.getItem('user_id') ?? '';

  useEffect(() => {
    loadStrategy();
    return () => {
      if (completionTimerRef.current) {
        window.clearTimeout(completionTimerRef.current);
      }
    };
  }, []);

  const applyResponseData = (data: any) => {
    const kwsObj = data?.keywords ?? {};
    setKeywordsObj(kwsObj);
    setIsLocked(data?.is_locked ?? false);
    setLockedUntil(data?.locked_until ?? null);
    setGenerationProgress(data?.progress ?? null);
    
    const allKws = [...(kwsObj.seo || []), ...(kwsObj.gmb || [])];
    setSelectedIds(allKws.filter((k: any) => k.is_selected).map((k: any) => k.id));
    if (!kwsObj[activeTab] && Object.keys(kwsObj).length > 0) {
      setActiveTab(Object.keys(kwsObj)[0] as 'seo' | 'gmb');
    }
  };

  const pollKeywordUniverse = async () => {
    try {
      const response = await apiService.getKeywordUniverse(userId);
      const data = response.data;
      setGenerationProgress(data?.progress ?? null);
      if (data?.status === 'generating') {
        setTimeout(pollKeywordUniverse, 5000);
      } else if (data?.status === 'error') {
        setGenerating(false);
        setLoading(false);
        setErrorMsg('An error occurred during keyword generation.');
      } else {
        setGenerationProgress({
          ...(data?.progress ?? {}),
          status: 'complete',
          step: 'complete',
          step_index: data?.progress?.total_steps ?? 9,
          total_steps: data?.progress?.total_steps ?? 9,
          percent: 100,
          title: 'Your keywords are ready',
          message: 'Final keyword universe prepared. Loading your review list now.',
          icon: 'ri-check-line',
          tone: 'success',
        });
        setShowCompletion(true);
        completionTimerRef.current = window.setTimeout(() => {
          applyResponseData(data);
          setGenerating(false);
          setLoading(false);
          setShowCompletion(false);
        }, 1500);
      }
    } catch (err: any) {
      setGenerating(false);
      setLoading(false);
      setErrorMsg(err.message ?? 'Failed to check status.');
    }
  };

  const loadStrategy = async () => {
    if (!userId) { setErrorMsg('User ID not found.'); setLoading(false); return; }
    try {
      if (!initialLoadComplete && Object.keys(keywordsObj).length === 0) {
        setLoading(true);
      }
      let response = await apiService.getKeywordUniverse(userId);
      let data = response.data;
      setGenerationProgress(data?.progress ?? null);

      if (data?.status === 'generating') {
        setGenerating(true);
        pollKeywordUniverse();
        return;
      }

      // Previous generation failed — show error state with Retry option
      if (data?.status === 'error') {
        setGenerating(false);
        setLoading(false);
        setGenerationProgress(data?.progress ?? null);
        setErrorMsg('Keyword generation failed. Click Retry to try again.');
        return;
      }

      // Handle missing or 'awaiting_enrichment' states by triggering initialization
      if (data?.status === 'not_initialized' || data?.universe_status === 'awaiting_enrichment') {
        if (!initTriggeredRef.current) {
          initTriggeredRef.current = true;
          setGenerating(true);
          setShowCompletion(false);
          const initResponse = await apiService.initKeywordUniverse(userId);
          setGenerationProgress(initResponse.data?.progress ?? null);
          pollKeywordUniverse();
        }
        return;
      }

      applyResponseData(data);
      setLoading(false);
      setInitialLoadComplete(true);
    } catch (err: any) {
      setLoading(false);
      setInitialLoadComplete(true);
      setErrorMsg(err.message ?? 'Failed to load keywords.');
    }
  };

  const toggleSelection = (id: number) => {
    if (isLocked) return;
    if (selectedIds.includes(id)) {
      trackAction(ACTIONS.DESELECT_KEYWORD, 'Keyword Deselected', { keyword_id: id });
      setSelectedIds(prev => prev.filter(i => i !== id));
      setInlineMsg(null);
    } else {
      if (selectedIds.length >= 20) {
        setInlineMsg('Deselect a keyword to add this one — maximum 20 can be selected');
        return;
      }
      setInlineMsg(null);
      trackAction(ACTIONS.SELECT_KEYWORD, 'Keyword Selected', { keyword_id: id });
      setSelectedIds(prev => [...prev, id]);
    }
  };

  const handleTypeFilterToggle = (label: string) => {
    const mappedType = label.toLowerCase().replace('-', '_'); // 'Short-tail' -> 'short_tail', 'Long-tail' -> 'long_tail', 'Branded' -> 'branded'
    if (typeFilter === mappedType) {
      setTypeFilter(null);
    } else {
      setTypeFilter(mappedType);
    }
  };

  const handleSortVolume = () => {
    if (sortBy === 'volume-desc') {
      setSortBy('volume-asc');
    } else if (sortBy === 'volume-asc') {
      setSortBy('score');
    } else {
      setSortBy('volume-desc');
    }
  };

  const handleSortScore = () => {
    if (sortBy === 'score') {
      setSortBy('score-asc');
    } else {
      setSortBy('score');
    }
  };

  const handleLockUniverse = async () => {
    if (!userId || selectedIds.length === 0 || selectedIds.length > 20) return;
    if (!confirm(`Lock these ${selectedIds.length} keywords for 90 days?`)) return;
    try {
      setLocking(true);
      trackAction(ACTIONS.LOCK_KEYWORD_UNIVERSE, 'Lock Keyword Universe', { 
        selected_count: selectedIds.length 
      });
      const res = await apiService.finalizeKeywordSelection(userId, selectedIds);
      if (res.status === 'success') {
        setIsLocked(true);
        setLockedUntil(res.data.locked_until);
        onLockSuccess?.();
      }
    } catch (err: any) {
      setErrorMsg('Failed to lock: ' + (err.message ?? ''));
    } finally {
      setLocking(false);
    }
  };

  const handleEditKeywords = async () => {
    if (!userId) return;

    try {
      trackAction(ACTIONS.EDIT_KEYWORDS, 'Edit Keywords/Refresh Universe');
      setUpdatingKeywords(true);
      setInlineMsg(null);
      await apiService.initKeywordUniverse(userId);
      setShowCompletion(false);
      setGenerationProgress({
        status: 'generating',
        step: 'profile',
        step_index: 1,
        total_steps: 9,
        percent: 8,
        title: 'Refreshing keyword universe',
        message: 'Restarting the planner with your latest profile context.',
        icon: 'ri-refresh-line',
        tone: 'teal',
      });
      setGenerating(true);
      pollKeywordUniverse();
    } catch (err: any) {
      setErrorMsg(err.message ?? 'Failed to update keywords.');
    } finally {
      setUpdatingKeywords(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const handleShowProvenance = async (kw: KeywordItem) => {
    try {
      onTooltipShow(
        `Audit Trace: ${kw.keyword}`,
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      );
      
      const res = await apiService.getKeywordProvenance(kw.id);
      const data = res.data || res;
      
      if (!data || !data.provenance) {
        onTooltipShow(
          `Audit Trace: ${kw.keyword}`,
          <div className="text-gray-500 italic">No audit trace available.</div>
        );
        return;
      }

      const prov = data.provenance;
      
      // Define which fields are significant to the user and their display labels
      const significantFields: Record<string, string> = {
        step2_relevance_score: 'Relevance Score',
        universe_rank: 'Universe Rank',
        traffic_potential: 'Traffic Potential',
        business_potential: 'Business Potential',
        segmentation_score: 'Segmentation Score',
        target_module: 'Target Module',
        routing_reason: 'Routing Reason',
        parent_topic: 'Parent Topic',
        dominant_intent: 'Dominant Intent',
        is_primary: 'Primary Keyword',
        rank_status: 'Rank Status',
        current_position: 'Current Position'
      };

      // Extract key metrics from stages to generate plain-english insights
      let relevanceScore = 0;
      let businessPotential = 0;
      let trafficPotential = 0;
      let universeRank = 99999;
      let dominantIntent = '';
      let currentPosition: number | null = null;
      let gscMatchType: string | null = null;

      let isCompetitorGap = prov.origin?.toLowerCase() === 'competitor';
      const isGscOrigin = prov.origin?.toLowerCase() === 'gsc';
      
      const rawPosition = prov.origin_detail?.position ?? kw.current_position;
      const gscPosition = rawPosition != null ? Math.round(Number(rawPosition) * 10) / 10 : null;
      const gscRankStatus = prov.origin_detail?.rank_status || kw.rank_status;
      
      prov.stages?.forEach((s: any) => {
        if (s.step2_relevance_score != null) relevanceScore = s.step2_relevance_score;
        if (s.business_potential != null) businessPotential = s.business_potential;
        if (s.traffic_potential != null) trafficPotential = s.traffic_potential;
        if (s.universe_rank != null) universeRank = s.universe_rank;
        if (s.dominant_intent != null) dominantIntent = s.dominant_intent;
        if (s.current_position != null) currentPosition = s.current_position;
        if (s.gsc_match_type != null) gscMatchType = s.gsc_match_type;
        if (s.competitor_match === true || s.is_competitor_gap === true || s.is_gap === true) isCompetitorGap = true;
      });

      const displayStages = [...(prov.stages || [])];
      const hasBrandMaturity = displayStages.some((s: any) => s.stage?.toLowerCase() === 'brand_maturity');
      if (isGscOrigin && !hasBrandMaturity) {
        displayStages.push({
          stage: 'brand_maturity',
          rank_status: gscRankStatus || null,
          current_position: gscPosition
        });
      }

      const deduplicatedStages = displayStages.filter(
        (stage: any, index: number, self: any[]) =>
          index === self.findIndex((s: any) => s.stage?.toLowerCase() === stage.stage?.toLowerCase())
      );

      // Determine if GSC is connected
      const allKeywords = Object.values(keywordsObj).flat();
      const hasGscSource = allKeywords.some(k => k.source === 'gsc' || k.gsc_clicks != null || k.gsc_impressions != null);
      const hasGscStage = prov.stages?.some((s: any) => s.gsc_match_type && s.gsc_match_type !== 'none');
      const isGscConnected = hasGscSource || hasGscStage;

      const insights = [];

      // Relevance Grading
      if (relevanceScore > 0) {
        let grade = 'D', desc = 'Low relevance';
        if (relevanceScore >= 90) { grade = 'A'; desc = 'Excellent relevance'; }
        else if (relevanceScore >= 70) { grade = 'B'; desc = 'Good relevance'; }
        else if (relevanceScore >= 50) { grade = 'C'; desc = 'Moderate relevance'; }
        insights.push({ icon: 'ri-focus-2-line', color: 'text-blue-600', bg: 'bg-blue-100', text: `Relevance: Grade ${grade} (${desc})` });
      }

      // Business Potential Grading
      if (businessPotential !== undefined && businessPotential !== null && businessPotential !== 0) {
        let bpLabel = 'Neutral';
        let bpColor = 'text-yellow-600';
        let bpBg = 'bg-yellow-100';
        if (businessPotential >= 3) { bpLabel = 'Best'; bpColor = 'text-green-700'; bpBg = 'bg-green-100'; }
        else if (businessPotential === 2) { bpLabel = 'Good'; bpColor = 'text-green-600'; bpBg = 'bg-green-50'; }
        else if (businessPotential === 1) { bpLabel = 'Neutral'; bpColor = 'text-yellow-600'; bpBg = 'bg-yellow-100'; }
        
        insights.push({ icon: 'ri-money-dollar-circle-line', color: bpColor, bg: bpBg, text: `Business Potential: ${bpLabel}` });
      }

      // Traffic Grading
      if (trafficPotential > 0) {
        let grade = 'D';
        if (trafficPotential >= 10000) grade = 'A';
        else if (trafficPotential >= 1000) grade = 'B';
        else if (trafficPotential >= 100) grade = 'C';
        insights.push({ icon: 'ri-bar-chart-grouped-line', color: 'text-purple-600', bg: 'bg-purple-100', text: `Traffic Potential: Grade ${grade}` });
      }

      // if (universeRank <= 100) {
      //   // insights.push({ icon: 'ri-medal-line', color: 'text-amber-600', bg: 'bg-amber-100', text: `Universe Rank: Top Tier (#${universeRank})` });
      // }
      
      // External sources (GSC / Competitor / GKP)
      if (isGscOrigin) {
        const positionVal = gscPosition != null ? gscPosition.toFixed(1) : 'Unknown';
        insights.unshift({
          icon: 'ri-google-fill',
          color: 'text-blue-600',
          bg: 'bg-blue-100',
          text: `Already driving organic visibility (Rank: ${positionVal})`
        });
      } else {
        if (gscMatchType && gscMatchType !== 'none') {
          if (currentPosition != null) {
            insights.unshift({ icon: 'ri-google-fill', color: 'text-blue-600', bg: 'bg-blue-100', text: `Already driving organic visibility (Rank: ${currentPosition})` });
          } else {
            insights.unshift({ icon: 'ri-google-fill', color: 'text-blue-600', bg: 'bg-blue-100', text: `Found in your Google Search Console history` });
          }
        }
        
        if (isCompetitorGap) {
          insights.unshift({ icon: 'ri-spy-line', color: 'text-red-600', bg: 'bg-red-100', text: 'Identified as a valuable competitor gap' });
        } else if (!gscMatchType || gscMatchType === 'none') {
          insights.unshift({ icon: 'ri-search-eye-line', color: 'text-indigo-500', bg: 'bg-indigo-50', text: 'Discovered via Google Keyword Planner database' });
        }
      }
      
      const content = (
        <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
          
          {insights.length > 0 && (
            <div className="bg-teal-50/50 p-4 rounded-xl border border-teal-100 relative">
              <div className="font-bold text-teal-800 mb-3 flex items-center gap-2">
                <i className="ri-sparkling-line"></i> Why was this selected?
              </div>
              <div className="space-y-2 mb-3">
                {insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${insight.bg} ${insight.color}`}>
                      <i className={insight.icon}></i>
                    </div>
                    <span className="text-gray-700 leading-snug">{insight.text}</span>
                  </div>
                ))}
              </div>
              <div className="pt-2 mt-2 border-t border-teal-100/60 text-xs text-teal-700/80 italic flex items-center gap-1.5">
                <i className="ri-information-line"></i>
                Check the "Grading Legend" button above to understand these grades.
              </div>
            </div>
          )}
          <div>
            <div className="font-semibold text-gray-900 dark:text-white mb-2">Meta Information</div>
            <div className="space-y-2">
              {(() => {
                const filtered = deduplicatedStages.filter((s: any) => ['routing', 'brand_maturity'].includes(s.stage?.toLowerCase()));
                const ordered: any[] = [];
                const routing = filtered.find((s: any) => s.stage?.toLowerCase() === 'routing');
                if (routing) ordered.push(routing);
                const brandMaturity = filtered.find((s: any) => s.stage?.toLowerCase() === 'brand_maturity');
                if (brandMaturity) ordered.push(brandMaturity);
                filtered.forEach((s: any) => {
                  const name = s.stage?.toLowerCase();
                  if (name !== 'routing' && name !== 'brand_maturity') {
                    ordered.push(s);
                  }
                });
                return ordered;
              })().map((stage: any, i: number) => {
                const isBrandMaturity = stage.stage?.toLowerCase() === 'brand_maturity';
                
                if (isBrandMaturity) {
                  const hasNoRank = stage.current_position === null || stage.current_position === undefined || stage.current_position === 0 || String(stage.rank_status).toLowerCase() === 'no_rank';
                  if (hasNoRank) {
                    return (
                      <div key={i} className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="font-semibold capitalize text-teal-700 dark:text-teal-400 mb-2 border-b border-gray-100 pb-1">Rank Status</div>
                        {isGscConnected ? (
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            Not currently ranking
                          </div>
                        ) : (
                          <div className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                            Connect GSC to see ranking data
                          </div>
                        )}
                      </div>
                    );
                  }
                }

                // Filter only significant fields present in this stage
                const stageFields = Object.entries(stage)
                  .filter(([k, v]) => significantFields[k] && v !== null && v !== '')
                  .map(([k, v]) => ({ key: k, label: significantFields[k], value: v }));

                if (stageFields.length === 0) return null; // Skip stage if no significant fields to show

                return (
                  <div key={i} className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="font-semibold capitalize text-teal-700 dark:text-teal-400 mb-2 border-b border-gray-100 pb-1">{stage.stage?.toLowerCase() === 'brand_maturity' ? 'Rank Status' : stage.stage?.replace(/_/g, ' ')}</div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-2 text-xs">
                      {stageFields.map(({ key, label, value }) => (
                        <div key={key} className="flex flex-col">
                          <span className="text-gray-500 text-[10px] uppercase tracking-wider">{label}</span>
                          <span className="font-medium text-gray-800 dark:text-gray-200 truncate" title={String(value)}>
                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
      
      onTooltipShow(`Audit Trace: ${kw.keyword}`, content);
    } catch (err: any) {
      onTooltipShow(
        `Audit Trace: ${kw.keyword}`,
        <div className="text-red-500">Failed to load audit trace.</div>
      );
    }
  };

  const getDifficultyColor = (d: string) => {
    if (!d) return 'text-gray-600 bg-gray-100';
    const dl = d.toLowerCase();
    if (dl === 'high') return 'text-red-600 bg-red-100';
    if (dl === 'medium') return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getKdLabelAndColor = (idx?: number) => {
    if (idx === undefined || idx === null) return null;
    if (idx <= 29) return { label: 'Easy', color: 'text-green-600 bg-green-100' };
    if (idx <= 59) return { label: 'Medium', color: 'text-yellow-600 bg-yellow-100' };
    return { label: 'Hard', color: 'text-red-600 bg-red-100' };
  };

  const getRelevanceColor = (r: string) => {
    const rl = r.toLowerCase();
    if (rl === 'high') return 'text-green-600 bg-green-100';
    if (rl === 'medium') return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getIntentColor = (i: string) => {
    const map: Record<string, string> = {
      transactional: 'text-teal-600 bg-teal-100',
      informational: 'text-indigo-600 bg-indigo-100',
      commercial: 'text-orange-600 bg-orange-100',
      navigational: 'text-blue-600 bg-blue-100',
    };
    return map[i?.toLowerCase()] ?? 'text-gray-600 bg-gray-100';
  };

  const getTypeColor = (t: string) => {
    const map: Record<string, string> = {
      local: 'bg-emerald-100 text-emerald-700',
      branded: 'bg-purple-100 text-purple-700',
      short_tail: 'bg-blue-100 text-blue-700',
      long_tail: 'bg-amber-100 text-amber-700',
    };
    return map[t?.toLowerCase()] ?? 'bg-gray-100 text-gray-500';
  };

  const getTypeIcon = (t: string) => {
    const map: Record<string, string> = {
      local: 'ri-map-pin-line',
      branded: 'ri-award-line',
      short_tail: 'ri-focus-3-line',
      long_tail: 'ri-text-spacing',
    };
    return map[t?.toLowerCase()] ?? 'ri-price-tag-3-line';
  };

  const hasLocalQualifier = (keyword: string) => {
    return /\bin\s+[a-z][a-z\s'-]{1,}$/i.test(keyword.trim());
  };

  
  const getProgressToneClasses = (tone?: string) => {
    const map: Record<string, string> = {
      blue: 'bg-blue-50 text-blue-700 border-blue-100',
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      teal: 'bg-teal-50 text-teal-700 border-teal-100',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
      violet: 'bg-violet-50 text-violet-700 border-violet-100',
      cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
      amber: 'bg-amber-50 text-amber-700 border-amber-100',
      rose: 'bg-rose-50 text-rose-700 border-rose-100',
      error: 'bg-red-50 text-red-700 border-red-100',
      success: 'bg-green-50 text-green-700 border-green-100',
    };
    return map[tone || 'teal'] ?? map.teal;
  };

  const handleShowGradingLegend = () => {
    const legendContent = (
      <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
        <div>
          <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2"><i className="ri-focus-2-line text-blue-500"></i> Relevance Score</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Grade A (≥90):</strong> Excellent relevance</li>
            <li><strong>Grade B (70-89):</strong> Good relevance</li>
            <li><strong>Grade C (50-69):</strong> Moderate relevance</li>
            <li><strong>Grade D (&lt;50):</strong> Low relevance</li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-4 flex items-center gap-2"><i className="ri-money-dollar-circle-line text-green-500"></i> Business Potential</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Best (3):</strong> High commercial value</li>
            <li><strong>Good (2):</strong> Moderate commercial intent</li>
            <li><strong>Neutral (1):</strong> Informational or mixed intent</li>
            <li><strong>Bad (0):</strong> Very low commercial value</li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-4 flex items-center gap-2"><i className="ri-bar-chart-grouped-line text-purple-500"></i> Traffic Potential</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Grade A:</strong> &gt; 10,000 monthly visits</li>
            <li><strong>Grade B:</strong> 1,000 - 10,000 monthly visits</li>
            <li><strong>Grade C:</strong> 100 - 999 monthly visits</li>
            <li><strong>Grade D:</strong> &lt; 100 monthly visits</li>
          </ul>
        </div>
      </div>
    );
    onTooltipShow('Keyword Grading Legend', legendContent);
  };

  // ---------------------------------------------------------------------------
  // Filters + sort
  // ---------------------------------------------------------------------------
  
  const keywords = keywordsObj[activeTab] || [];
  const allKeywords = [...(keywordsObj.seo || []), ...(keywordsObj.gmb || [])];

  const filtered = keywords.filter(k => {
    if (typeFilter) {
      const kwType = (k.keyword_type ?? k.type ?? '').toLowerCase();
      if (kwType !== typeFilter) return false;
    }
    if (relevanceFilter) {
      const rating = (k.relevance_insight?.rating ?? '').toLowerCase();
      if (rating !== relevanceFilter) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'volume-desc') return b.volume - a.volume;
    if (sortBy === 'volume-asc') return a.volume - b.volume;
    if (sortBy === 'score-asc') {
      const scoreA = Number(a.brand_relevance_score) || 0;
      const scoreB = Number(b.brand_relevance_score) || 0;
      return scoreA - scoreB;
    }
    if (sortBy === 'difficulty') return (['high', 'medium', 'low'].indexOf(b.difficulty?.toLowerCase()) - ['high', 'medium', 'low'].indexOf(a.difficulty?.toLowerCase()));
    
    // default: score (descending brand_relevance_score)
    const scoreA = Number(a.brand_relevance_score) || 0;
    const scoreB = Number(b.brand_relevance_score) || 0;
    return scoreB - scoreA;
  });

  // Stat buckets — computed from the active display pool
  const activePool = (isLocked || selectedIds.length > 0) ? allKeywords.filter(k => selectedIds.includes(k.id)) : allKeywords;
  const ltCount = activePool.filter(k => (k.keyword_type ?? k.type)?.toLowerCase() === 'long_tail').length;
  const stCount = activePool.filter(k => (k.keyword_type ?? k.type)?.toLowerCase() === 'short_tail').length;
  const brandCount = activePool.filter(k => (k.keyword_type ?? k.type)?.toLowerCase() === 'branded').length;

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  if (loading && !generating && !showCompletion) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-20 text-center shadow-sm">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4"></div>
        <p className="text-sm font-medium text-gray-600">Fetching keyword universe...</p>
      </div>
    );
  }

  if (generating || showCompletion) {
    const progress = generationProgress ?? {
      step_index: 1,
      total_steps: 9,
      percent: 12,
      title: 'Starting keyword planner',
      message: 'Preparing the V2 planner and checking your latest onboarding context.',
      icon: 'ri-magic-line',
      tone: 'teal',
    };
    const isComplete = showCompletion || progress.status === 'complete' || Number(progress.percent ?? 0) >= 100;
    const percent = isComplete ? 100 : Math.min(99, Math.max(0, Number(progress.percent ?? 0)));

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-8 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              {isComplete ? (
                <div className="relative w-12 h-12 shrink-0">
                  <span className="absolute inset-0 rounded-full bg-green-300 animate-ping opacity-40"></span>
                  <div className="relative w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg shadow-green-500/25 transition-transform duration-500 scale-100">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                </div>
              ) : (
                <div className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 ${getProgressToneClasses(progress.tone)}`}>
                  <i className={`${progress.icon ?? 'ri-loader-4-line'} text-2xl`}></i>
                </div>
              )}
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                  Step {progress.step_index ?? 1} of {progress.total_steps ?? 9}
                </div>
                <h2 className={`text-2xl font-bold ${isComplete ? 'text-green-700' : 'text-gray-900 dark:text-white'}`}>
                  {isComplete ? 'Your keywords are ready' : progress.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                  {isComplete ? 'Final keyword universe prepared. Loading your review list now.' : progress.message}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-3xl font-bold ${isComplete ? 'text-green-600' : 'text-gray-900 dark:text-white animate-pulse'}`}>
                {percent}%
              </div>
              <div className="text-xs font-medium text-gray-400">{isComplete ? 'complete' : 'live progress'}</div>
            </div>
          </div>

          <div className="mt-7">
            <div className="h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ease-out ${isComplete ? 'bg-green-500' : 'bg-teal-600'}`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="px-8 py-5 bg-gray-50 dark:bg-gray-900/30 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-500"></span>
            </span>
            {isComplete ? 'Done. Opening your keyword universe.' : 'The planner is still working in the background. This usually takes 5 to 6 minutes.'}
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-refresh-line"></i>
            Refreshes automatically
          </div>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3">
        <i className="ri-error-warning-line text-lg shrink-0" />
        <span><strong>Error: </strong>{errorMsg}</span>
        <button
          onClick={() => {
            setErrorMsg(null);
            setGenerationProgress(null);
            initTriggeredRef.current = false;
            handleEditKeywords();
          }}
          className="ml-auto text-sm font-medium underline hover:no-underline whitespace-nowrap"
        >
          Retry
        </button>
      </div>
    );
  }

        // ── STEP 2: LOCKED ────────────────────────────────────────────────────────

        if (isLocked) {
    const lockedKws = allKeywords
      .filter(k => selectedIds.includes(k.id))
      .filter(k => {
        if (typeFilter) {
          const kwType = (k.keyword_type ?? k.type ?? '').toLowerCase();
          return kwType === typeFilter;
        }
        return true;
      });
        return (
        <div className="space-y-6">
          {/* Hero banner */}
          <div className="bg-emerald-500 rounded-xl p-10 shadow-lg text-white text-center relative overflow-hidden">
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <i className="ri-star-fill text-3xl" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Your Keyword Universe is Locked!</h2>
              <p className="text-white/90 text-lg mb-4">
                These {lockedKws.length} keywords will guide your SEO strategy for the next 3 months
              </p>
              {lockedUntil && <p className="text-sm font-medium opacity-80">Locked until: {lockedUntil}</p>}
            </div>
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2 blur-2xl" />
          </div>

          {/* Stat buckets */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Short-tail', count: stCount, icon: 'ri-focus-3-line', color: 'text-blue-600', activeBg: 'bg-blue-50 border-blue-500 ring-2 ring-blue-100' },
              { label: 'Long-tail', count: ltCount, icon: 'ri-text-spacing', color: 'text-amber-600', activeBg: 'bg-amber-50 border-amber-500 ring-2 ring-amber-100' },
              { label: 'Branded', count: brandCount, icon: 'ri-award-line', color: 'text-purple-600', activeBg: 'bg-purple-50 border-purple-500 ring-2 ring-purple-100' },
            ].map(s => {
              const mappedType = s.label.toLowerCase().replace('-', '_');
              const isActive = typeFilter === mappedType;
              return (
                <div
                  key={s.label}
                  onClick={() => handleTypeFilterToggle(s.label)}
                  className={`rounded-xl p-6 text-center shadow-lg border cursor-pointer transition-all select-none ${
                    isActive
                      ? s.activeBg
                      : 'bg-white border-gray-200 hover:border-teal-300 dark:bg-gray-800 dark:border-gray-700'
                  }`}
                >
                  <i className={`${s.icon} ${s.color} text-2xl mb-2 block`} />
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{s.count}</div>
                  <div className="text-gray-500 dark:text-gray-400 text-sm">{s.label}</div>
                </div>
              );
            })}
          </div>

          {/* Keyword grid */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="mb-5 pb-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <h3 className="text-lg font-bold text-gray-900">Your Complete Keyword Universe</h3>
              {/* <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                LOW tag means difficulty is low
              </span> */}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {lockedKws.map((kw, idx) => (
                <div key={kw.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-teal-400/60 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                      <span>{idx + 1}. {kw.keyword}</span>
                      <button onClick={() => handleShowProvenance(kw)} className="text-gray-400 hover:text-teal-500 transition-colors flex-shrink-0" title="View Audit Trace">
                        <i className="ri-information-line"></i>
                      </button>
                      {hasLocalQualifier(kw.keyword) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
                          GMB
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-500">
                        {kw.volume !== undefined && kw.volume !== null ? kw.volume.toLocaleString() : '-'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getIntentColor(kw.intent)}`}>
                        {kw.intent}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        );
  }

        // ── STEP 1: SELECTION ─────────────────────────────────────────────────────

        return (
        <div className="space-y-6">
          {/* Page header */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Keyword Planner</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleShowGradingLegend}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                >
                  <i className="ri-list-check"></i> Grading Legend
                </button>
                <button
                  onClick={() => onTooltipShow('Keyword Planner', 'Select up to 20 keywords to lock your universe for 90 days.')}
                  className="text-gray-400 hover:text-teal-500 transition-colors"
                >
                  <i className="ri-information-line text-xl" />
                </button>
              </div>
            </div>
            <p className="text-gray-500 text-sm">Select up to 20 keywords to lock your universe.</p>
          </div>

          {/* Inline cap message */}
          {inlineMsg && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2.5 rounded-lg text-sm">
              {inlineMsg}
              <button onClick={() => setInlineMsg(null)} className="ml-3 text-xs underline">Dismiss</button>
            </div>
          )}

          {/* Stat buckets */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Short-tail', count: stCount, icon: 'ri-focus-3-line', color: 'text-blue-600', activeBg: 'bg-blue-50 border-blue-500 ring-2 ring-blue-100' },
              { label: 'Long-tail', count: ltCount, icon: 'ri-text-spacing', color: 'text-amber-600', activeBg: 'bg-amber-50 border-amber-500 ring-2 ring-amber-100' },
              { label: 'Branded', count: brandCount, icon: 'ri-award-line', color: 'text-purple-600', activeBg: 'bg-purple-50 border-purple-500 ring-2 ring-purple-100' },
            ].map(s => {
              const mappedType = s.label.toLowerCase().replace('-', '_');
              const isActive = typeFilter === mappedType;
              return (
                <div
                  key={s.label}
                  onClick={() => handleTypeFilterToggle(s.label)}
                  className={`rounded-xl p-5 text-center shadow border cursor-pointer transition-all select-none ${
                    isActive
                      ? s.activeBg
                      : 'bg-white border-gray-200 hover:border-teal-300 dark:bg-gray-800 dark:border-gray-700'
                  }`}
                >
                  <i className={`${s.icon} ${s.color} text-2xl mb-1 block`} />
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{s.count}</div>
                  <div className="text-gray-500 dark:text-gray-400 text-sm">{s.label}</div>
                </div>
              );
            })}
          </div>

          {/* Selection table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            
            {/* Tabs */}
            {Object.keys(keywordsObj).length > 0 && (
              <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {Object.keys(keywordsObj).map(key => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key as any)}
                    className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 ${
                      activeTab === key
                        ? 'border-teal-500 text-teal-600 bg-white dark:bg-gray-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {key === 'seo' ? 'SEO Keywords' : 'GMB Keywords'}
                  </button>
                ))}
              </div>
            )}

            {/* Table toolbar */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-teal-50 to-white dark:from-teal-900/20 dark:to-gray-800">
              <div className="flex gap-3">
                {/* Volume sort control */}
                <button
                  onClick={handleSortVolume}
                  className={`px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5 text-sm font-medium transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    sortBy.startsWith('volume') ? 'bg-teal-50 border-teal-300 text-teal-600' : 'bg-white text-gray-700'
                  }`}
                  title="Sort by Volume"
                >
                  <span>Volume</span>
                  {sortBy === 'volume-asc' ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : sortBy === 'volume-desc' ? (
                    <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {/* Score sort control */}
                <button
                  onClick={handleSortScore}
                  className={`px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5 text-sm font-medium transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    sortBy.startsWith('score') ? 'bg-teal-50 border-teal-300 text-teal-600' : 'bg-white text-gray-700'
                  }`}
                  title="Sort by Score"
                >
                  <span>Score</span>
                  {sortBy === 'score-asc' ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : sortBy === 'score' ? (
                    <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {/* Relevance Insight filter control */}
                <div className="flex items-center gap-1.5 ml-2 border-l border-gray-200 pl-3">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Relevance:</span>
                  <div className="flex gap-1">
                    {(['High', 'Medium', 'Low'] as const).map(rating => {
                      const isActive = relevanceFilter === rating.toLowerCase();
                      
                      let activeStyle = '';
                      if (rating === 'High') {
                        activeStyle = 'bg-green-500 text-white border-green-500 hover:bg-green-600';
                      } else if (rating === 'Medium') {
                        activeStyle = 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600';
                      } else {
                        activeStyle = 'bg-slate-500 text-white border-slate-500 hover:bg-slate-600';
                      }
                      
                      return (
                        <button
                          key={rating}
                          onClick={() => {
                            if (relevanceFilter === rating.toLowerCase()) {
                              setRelevanceFilter(null);
                            } else {
                              setRelevanceFilter(rating.toLowerCase());
                            }
                          }}
                          className={`px-2.5 py-1 border text-xs font-bold rounded-lg transition-all ${
                            isActive 
                              ? activeStyle 
                              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600'
                          }`}
                        >
                          {rating}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm font-semibold px-3 py-1.5 rounded-full ${selectedIds.length > 0 && selectedIds.length <= 20 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                  {selectedIds.length} / 20 selected
                </span>
                <button
                  onClick={handleLockUniverse}
                  disabled={selectedIds.length === 0 || selectedIds.length > 20 || locking}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${selectedIds.length > 0 && selectedIds.length <= 20 && !locking
                    ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                >
                  {locking ? 'Locking…' : 'Lock Keyword Universe →'}
                </button>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-600">
                  <th className="w-12 px-4 py-3 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest">Select</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Keyword</th>
                  <th 
                    onClick={handleSortVolume}
                    className="px-3 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer hover:text-gray-600 select-none"
                  >
                    <div className="flex items-center gap-1">
                      <span>GKP Volume</span>
                      {sortBy === 'volume-asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : sortBy === 'volume-desc' ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-300" />
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest" title="Keyword Difficulty (0-100)">KD</th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Intent</th>
                  <th 
                    onClick={handleSortScore}
                    className="px-3 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest cursor-pointer hover:text-gray-600 select-none" 
                    title="Brand Relevance Score — how well this keyword matches your business"
                  >
                    <div className="flex items-center gap-1">
                      <span>Score</span>
                      {sortBy === 'score' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : sortBy === 'score-asc' ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-300" />
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest" title="AI-assessed relevance rating for your brand">Relevance Insight</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(kw => {
                  const selected = selectedIds.includes(kw.id);
                  const kdInfo = getKdLabelAndColor(kw.competition_index);
                  return (
                    <tr
                      key={kw.id}
                      onClick={() => toggleSelection(kw.id)}
                      className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${selected
                        ? 'bg-teal-50 dark:bg-teal-900/10 hover:bg-teal-100/70'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                        }`}
                    >
                      <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelection(kw.id)}
                          disabled={!selected && selectedIds.length >= 20}
                          className="h-4 w-4 text-teal-600 rounded border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${selected ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                            {kw.keyword}
                          </span>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleShowProvenance(kw); }} 
                            className="text-gray-400 hover:text-teal-500 transition-colors" 
                            title="View Audit Trace"
                          >
                            <i className="ri-information-line"></i>
                          </button>
                        </div>
                        <div className="mt-1">
                          {kw.is_local && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
                              GMB
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {kw.volume !== undefined && kw.volume !== null ? kw.volume.toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-3">
                        {kdInfo ? (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${kdInfo.color}`}>
                            {kw.competition_index} {kdInfo.label}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getIntentColor(kw.intent)}`}>
                          {kw.intent}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {kw.brand_relevance_score !== undefined && kw.brand_relevance_score !== null ? (
                          <div className="flex flex-col gap-1 w-20">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                              {Number(kw.brand_relevance_score).toFixed(1)}
                            </span>
                            <div className="w-full h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-teal-500 rounded-full" 
                                style={{ width: `${Math.min(100, Math.max(0, kw.brand_relevance_score))}%` }} 
                              />
                            </div>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3">
                        {(() => {
                          const rating = kw.relevance_insight?.rating;
                          if (!rating) return '—';
                          const rLower = rating.toLowerCase();
                          let badgeStyle = 'bg-slate-500 text-white';
                          if (rLower === 'high') {
                            badgeStyle = 'bg-green-500 text-white';
                          } else if (rLower === 'medium') {
                            badgeStyle = 'bg-amber-500 text-white';
                          }
                          return (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${badgeStyle}`}>
                              {rating}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
}
